import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Mail, Lock, Phone, MapPin, AlertCircle, GraduationCap, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "educator" ? "educator" : "parent";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isParent = role === "parent";
  const title = useMemo(
    () => (isParent ? t("auth.createTitleParent") : t("auth.createTitleEducator")),
    [isParent, t]
  );

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, role });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center py-12 relative overflow-hidden bg-white" data-testid="register-page">
      <span className="geo-shape bg-[#16A34A]/15 h-72 w-72 -top-20 -left-20" aria-hidden />
      <span className="geo-shape bg-[#16A34A]/10 h-64 w-64 -bottom-20 -right-20" aria-hidden />

      <div className="relative w-full max-w-xl mx-auto px-4">
        <Link to="/" className="flex items-center justify-center mb-8">
          <Logo size={42} />
        </Link>

        <div className="bg-white rounded-3xl border border-[#E5EBE7] shadow-xl p-8">
          <h1 className="font-display text-3xl font-bold text-[#0A2518]">{title}</h1>
          <p className="mt-2 text-[#4B5563]">{t("auth.createSubtitle")}</p>

          {/* Role switch */}
          <div className="mt-6 grid grid-cols-2 gap-3" data-testid="register-role-switch">
            <button
              type="button"
              onClick={() => setRole("parent")}
              className={`p-4 rounded-xl border text-left transition-all ${
                isParent ? "border-[#16A34A] bg-[#F0FDF4]" : "border-[#E5EBE7] hover:border-[#16A34A]/50"
              }`}
              data-testid="register-role-parent"
            >
              <Heart className={`h-5 w-5 mb-2 ${isParent ? "text-[#16A34A]" : "text-[#4B5563]"}`} />
              <div className="font-display font-semibold text-[#0A2518]">{t("auth.iAmParent")}</div>
              <div className="text-xs text-[#4B5563]">{t("auth.iAmParentSub")}</div>
            </button>
            <button
              type="button"
              onClick={() => setRole("educator")}
              className={`p-4 rounded-xl border text-left transition-all ${
                !isParent ? "border-[#16A34A] bg-[#F0FDF4]" : "border-[#E5EBE7] hover:border-[#16A34A]/50"
              }`}
              data-testid="register-role-educator"
            >
              <GraduationCap className={`h-5 w-5 mb-2 ${!isParent ? "text-[#16A34A]" : "text-[#4B5563]"}`} />
              <div className="font-display font-semibold text-[#0A2518]">{t("auth.iAmEducator")}</div>
              <div className="text-xs text-[#4B5563]">{t("auth.iAmEducatorSub")}</div>
            </button>
          </div>

          <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="name" className="text-[#0A2518]">{t("auth.fullName")}</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input id="name" name="name" required value={form.name} onChange={handleChange} className="pl-9 h-11" data-testid="register-name-input" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="email" className="text-[#0A2518]">{t("auth.email")}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className="pl-9 h-11" data-testid="register-email-input" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="password" className="text-[#0A2518]">{t("auth.password")}</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input id="password" name="password" type="password" required minLength={6} value={form.password} onChange={handleChange} className="pl-9 h-11" data-testid="register-password-input" />
              </div>
              <p className="mt-1 text-xs text-[#4B5563]">{t("auth.passwordHint")}</p>
            </div>
            <div>
              <Label htmlFor="phone" className="text-[#0A2518]">{t("auth.phone")}</Label>
              <div className="relative mt-1.5">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input id="phone" name="phone" value={form.phone} onChange={handleChange} className="pl-9 h-11" data-testid="register-phone-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="city" className="text-[#0A2518]">{t("auth.city")}</Label>
              <div className="relative mt-1.5">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input id="city" name="city" value={form.city} onChange={handleChange} className="pl-9 h-11" placeholder={t("auth.cityPlaceholder")} data-testid="register-city-input" />
              </div>
            </div>

            {error && (
              <div className="sm:col-span-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="register-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="sm:col-span-2">
              <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="register-submit-btn">
                {loading ? t("auth.createLoading") : t("auth.createBtn")}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-sm text-center text-[#4B5563]">
            {t("auth.alreadyRegistered")}{" "}
            <Link to="/login" className="text-[#16A34A] font-medium hover:underline" data-testid="register-go-login">
              {t("auth.loginLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
